import React, { useEffect, useState } from "react";
import { string } from "prop-types";
import { useSelector } from "react-redux";
import get from "lodash/get";

import { EPAPER_BANNER_TEXT } from "../../../constants";
import "./epaper-banner.m.css";

const EPAPER_BASE_URL = "https://epaper.prajavani.net/subscribe";

const buildEpaperUrl = (utmMedium) => {
  // Keep param order as: utm_source, utm_medium, utm_campaign
  return `${EPAPER_BASE_URL}?utm_source=pvsite&utm_medium=${utmMedium}&utm_campaign=awareness`;
};

// Helper function to strip anchor tags from HTML while preserving inner content
const stripAnchorTags = (html) => {
  if (!html) return html;
  // Remove opening <a> tags with any attributes
  let cleaned = html.replace(/<a\s[^>]*>/gi, "");
  // Remove closing </a> tags
  cleaned = cleaned.replace(/<\/a>/gi, "");
  return cleaned;
};

// Shared banner text content component
const BannerTextContent = ({ headlineClassName, subheadlineClassName }) => {

  // Verify constants are loaded
  if (!EPAPER_BANNER_TEXT || !EPAPER_BANNER_TEXT.HEADLINE || !EPAPER_BANNER_TEXT.SUBHEADLINE) {
    console.error("EPAPER_BANNER_TEXT constants are missing or incomplete:", EPAPER_BANNER_TEXT);
    return (
      <>
        <div styleName={headlineClassName}>{EPAPER_BANNER_TEXT.HEADLINE}</div>
        <div styleName={subheadlineClassName}>{EPAPER_BANNER_TEXT.SUBHEADLINE}</div>
      </>
    );
  }

  return (
    <>
      <div styleName={headlineClassName}>{EPAPER_BANNER_TEXT.HEADLINE}</div>
      <div styleName={subheadlineClassName}>{EPAPER_BANNER_TEXT.SUBHEADLINE}</div>
    </>
  );
};

const FEATURES = [
  {
    title: EPAPER_BANNER_TEXT.PAPER_BANNER_FEATURE_1_TITLE,
    subtitle: EPAPER_BANNER_TEXT.PAPER_BANNER_FEATURE_1_SUBTITLE
  },
  {
    title: EPAPER_BANNER_TEXT.PAPER_BANNER_FEATURE_2_TITLE,
    subtitle: EPAPER_BANNER_TEXT.PAPER_BANNER_FEATURE_2_SUBTITLE
  },
  {
    title: EPAPER_BANNER_TEXT.PAPER_BANNER_FEATURE_3_TITLE,
    subtitle: EPAPER_BANNER_TEXT.PAPER_BANNER_FEATURE_3_SUBTITLE
  }
];

const FeatureItem = ({ title, subtitle }) => (
  <div styleName="epaper-feature">
    <span styleName="epaper-feature-bar" />
    <div styleName="epaper-feature-text">
      <p styleName="epaper-feature-title">{title}</p>
      <p styleName="epaper-feature-subtitle">{subtitle}</p>
    </div>
  </div>
);

const EPaperBanner = ({ utmMedium = "topbanner", className = "", variant = "page", pageType, isHardcodedHeader }) => {
  const allowPageList = ["home-page", "section-page", "sub-section-page"];
  const shouldRenderCode = allowPageList.includes(pageType) && isHardcodedHeader !== "Canara-Robeco";

  if (!shouldRenderCode && variant === "page") return null;
  const epaperUrl = buildEpaperUrl(utmMedium);
  const [newspaperImageError, setNewspaperImageError] = useState(false);
  const [smartphoneImageError, setSmartphoneImageError] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const cmsContent = useSelector((state) =>
    get(state, ["qt", "config", "publisher-attributes", "epaper-banner-content"], null)
  );

  useEffect(() => {
    if (window && window.innerWidth > 767) {
      setIsDesktop(true);
    } else {
      setIsDesktop(false);
    }
  }, []);

  // Debug: Log rendering
  React.useEffect(() => {
    console.log("EPaperBanner component mounted/updated:", {
      utmMedium,
      variant,
      hasCmsContent: !!cmsContent,
      cmsContentLength: cmsContent?.length || 0,
      cmsContentValue: cmsContent
    });

    // Verify banner is in DOM
    if (typeof window !== "undefined") {
      setTimeout(() => {
        const banner = document.querySelector(
          '[styleName*="banner-wrapper"], .banner-wrapper, a[href*="epaper.prajavani.net"]'
        );
        console.log("EPaperBanner DOM check:", {
          bannerExists: !!banner,
          bannerVisible: banner ? window.getComputedStyle(banner).display !== "none" : false,
          bannerHeight: banner ? banner.offsetHeight : 0,
          bannerBackground: banner ? window.getComputedStyle(banner).backgroundColor : null
        });
      }, 100);
    }
  }, [utmMedium, variant, cmsContent]);

  const desktopImage = "https://tpml-sites.s3.ap-south-1.amazonaws.com/images/pv_dsktp_homesctns.png";
  const mobileImage = "https://tpml-sites.s3.ap-south-1.amazonaws.com/images/pv_mw_homesctns.png";

  // If CMS content is provided (HTML), use it
  // Only use CMS content if it's not empty/whitespace and contains actual visible content
  // Check for common empty HTML patterns
  const isEmptyHtml =
    cmsContent &&
    (cmsContent.trim() === "" ||
      cmsContent.trim() === "<div></div>" ||
      cmsContent.trim() === "<div> </div>" ||
      cmsContent.trim() === "<div></div>" ||
      cmsContent.replace(/<[^>]*>/g, "").trim() === ""); // Remove all HTML tags and check if any text remains

  if (cmsContent && typeof cmsContent === "string" && cmsContent.trim().length > 0 && !isEmptyHtml) {
    const cleanedContent = stripAnchorTags(cmsContent);
    return (
      <a
        href={epaperUrl}
        target="_blank"
        rel="noopener noreferrer"
        styleName={`banner-wrapper ${variant === "article" ? "banner-article" : ""}`}
        className={className}
        dangerouslySetInnerHTML={{ __html: cleanedContent }}
        style={{ minHeight: "92px", display: "block" }}
      />
    );
  }

  // In-article variant (simpler layout without newspaper icon)
  if (variant === "article") {
    return (
      <a
        href={epaperUrl}
        target="_blank"
        rel="noopener noreferrer"
        styleName={`banner-wrapper banner-article ${className}`}
      >
        <div styleName="banner-content banner-content-article">
          <div styleName="banner-left-article">
            <div styleName="banner-text-article">
              <BannerTextContent
                headlineClassName="banner-headline-article"
                subheadlineClassName="banner-subheadline-article"
              />
            </div>
          </div>
          <div styleName="banner-divider-article"></div>
          <div styleName="banner-right-article">
            {!smartphoneImageError && isDesktop ? (
              <img
                src="https://tpml-sites.s3.ap-south-1.amazonaws.com/images/pv+mobile.png"
                alt="ePaper on Smartphone"
                styleName="smartphone-image-article"
                loading="eager"
                onError={() => setSmartphoneImageError(true)}
              />
            ) : null}
            <div styleName="banner-cta-wrapper-article">
              <span styleName="banner-cta-button-article">
                <span styleName="mob-button-text-article">{EPAPER_BANNER_TEXT.BUTTON_MOBILE}</span>
                <span styleName="dsk-button-text-article">{EPAPER_BANNER_TEXT.BUTTON_DESKTOP}</span>
              </span>
            </div>
          </div>
        </div>
      </a>
    );
  }

  // Default banner content for home page (with newspaper icon)
  return (
    <a href={epaperUrl} target="_blank" rel="noopener noreferrer" styleName="banner-wrapper" className={className}>
      <div styleName="banner-content">
        <div styleName="banner-left">
          <div styleName="newspaper-image" />
          <div styleName="banner-center">
            <BannerTextContent headlineClassName="banner-headline" subheadlineClassName="banner-subheadline" />
          </div>
        </div>
        <div styleName="epaper-features">
          {FEATURES.map((item, index) => (
            <FeatureItem key={index} {...item} />
          ))}
        </div>

        <div styleName="banner-right">
          <div styleName="banner-cta-wrapper">
            <span styleName="banner-cta-button">
              <span className="hide-mobile">ಈಗಲೇ ಚಂದಾದಾರರಾಗಿ</span>
              <span className="hide-desktop">ಚಂದಾದಾರರಾಗಿ</span>
            </span>
          </div>
        </div>
      </div>
    </a>
  );
};

EPaperBanner.propTypes = {
  utmMedium: string,
  className: string,
  variant: string,
  pageType: string,
  isHardcodedHeader: string
};

export default EPaperBanner;
